import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { getClientCampaigns } from '../../api/clientPortal';
import { useClientTheme } from '../../contexts/ClientThemeContext';
import { Spinner, ErrorBanner } from './ClientDashboardPage';

function StatusBadge({ status = 'draft', T }) {
  const map = {
    active: { bg: 'rgba(34,197,94,0.12)',   color: 'rgba(21,128,61,0.9)',   border: 'rgba(34,197,94,0.3)' },
    open:   { bg: 'rgba(201,168,76,0.12)',  color: T.gold,                  border: 'rgba(201,168,76,0.3)' },
    draft:  { bg: T.id === 'light' ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.05)', color: T.textSub, border: T.border },
    closed: { bg: T.id === 'light' ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.04)', color: T.textMuted, border: T.border },
  };
  const s = map[status] || map.draft;
  return (
    <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', padding: '3px 9px', borderRadius: 999, background: s.bg, color: s.color, border: `1px solid ${s.border}` }}>
      {status}
    </span>
  );
}

export default function ClientCampaignsPage() {
  const navigate = useNavigate();
  const { theme: T } = useClientTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [campaigns, setCampaigns] = useState([]);

  useEffect(() => {
    let active = true;
    getClientCampaigns()
      .then((data) => { if (active) setCampaigns(Array.isArray(data?.campaigns) ? data.campaigns : []); })
      .catch((err) => { if (active) setError(err?.response?.data?.error || 'Failed to load campaigns.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  if (loading) return <Spinner T={T} />;
  if (error) return <ErrorBanner T={T}>{error}</ErrorBanner>;

  return (
    <div>
      <div style={{ fontSize: 20, fontWeight: 700, color: T.text, marginBottom: 20 }}>Campaigns</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {campaigns.map((c) => {
          const id = c._id || c.id;
          const start = c.campaignWindow?.startDate ? new Date(c.campaignWindow.startDate).toLocaleDateString() : null;
          const end   = c.campaignWindow?.endDate   ? new Date(c.campaignWindow.endDate).toLocaleDateString()   : null;
          return (
            <button
              key={id}
              onClick={() => navigate(`/client-portal/campaigns/${id}`)}
              style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', borderRadius: 10, border: `1px solid ${T.border}`, background: T.surface, cursor: 'pointer', textAlign: 'left', color: 'inherit', width: '100%', transition: 'border-color 0.12s, background 0.12s' }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = T.borderHover; e.currentTarget.style.background = T.surfaceHover; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.background = T.surface; }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 3 }}>{c.name}</div>
                {c.description && <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.description}</div>}
                {start && <div style={{ fontSize: 11, color: T.textMuted }}>{start}{end ? ` — ${end}` : ''}</div>}
              </div>
              <StatusBadge status={c.status} T={T} />
              <ChevronRight size={14} color={T.textMuted} />
            </button>
          );
        })}
        {!campaigns.length && <div style={{ padding: '24px 0', textAlign: 'center', fontSize: 13, color: T.textMuted }}>No campaigns available yet.</div>}
      </div>
    </div>
  );
}
